import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { environment } from 'shared/environments/environment';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import {
  catchError,
  combineLatest,
  filter,
  map,
  of,
  switchMap,
  take,
  throwError,
  timeout,
} from 'rxjs';

// Endpoints called by the public /credits pages, where the visitor never has a session
// (mobile deep links): a 401 from these must surface to the caller instead of bouncing the
// user to the login screen. Every other anonymous 401 keeps the legacy login redirect —
// it means an authenticated session died while the user sat on a guarded page.
// Covers GET /credits (catalog), POST /credits/purchase and POST /credits/payment-intent.
const PUBLIC_ENDPOINT_PATHS = ['/credits'];

const isPublicEndpoint = (url: string): boolean =>
  PUBLIC_ENDPOINT_PATHS.some(path => url.includes(path));

// Every call to our own backend declares the web client version so the service can gate
// features per client (see peech-services buildWebClientHeaders). Third-party hosts must
// not get it: a foreign custom header widens their CORS preflight for nothing.
const isPeechBackend = (url: string): boolean =>
  url.startsWith(environment.serviceUrl) || url.startsWith(environment.gatewayUrl);

// Upper bound on the token refresh in the 401 retry below. A refresh that never settles must
// not strand the caller — past this point the original 401 is surfaced instead.
const TOKEN_REFRESH_TIMEOUT_MS = 10_000;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  const request = isPeechBackend(req.url)
    ? req.clone({ headers: req.headers.set('client-version', environment.appVersion) })
    : req;

  // token$ is a BehaviorSubject that never completes, so switch-mapping it directly made every
  // later emission (Firebase token rotation, or a sign-in publishing the token) cancel the
  // in-flight request and re-issue it: duplicate GETs, duplicated side effects for
  // non-idempotent POSTs like /credits/payment-intent, and a subscription that never ended.
  // Take exactly one token and unsubscribe.
  //
  // The authInitialized$ gate keeps the one useful effect of that re-issue: a request that
  // starts while Firebase is still restoring the session used to be replayed once the token
  // landed. Now it waits for the session to settle instead, so it isn't sent anonymously.
  // This relies on AuthService publishing the token BEFORE flipping authInitialized$ — see
  // resolveTokenAndPublish(). Without that ordering this take(1) would consume token$'s
  // initial null and dispatch boot-time requests anonymously, which public endpoints would
  // happily answer in the wrong scope instead of 401ing into the retry below.
  //
  // The identity comes along for the ride: the 401 retry below has to know which session this
  // request was actually sent as.
  const dispatchSession$ = auth.authInitialized$.pipe(
    filter(initialized => initialized),
    take(1),
    switchMap(() => combineLatest([auth.user$, auth.token$]).pipe(take(1))),
    map(([user, token]) => ({ uid: user?.uid ?? null, token }))
  );

  return dispatchSession$.pipe(
    switchMap(({ uid, token }) =>
      next(
        token ? request.clone({ headers: request.headers.set('Authorization', token) }) : request
      ).pipe(
        catchError(error => {
          if (error.status !== 401) {
            return throwError(() => error);
          }

          // take(1) for the same reason as above: user$ never completes, so without it a later
          // identity emission re-ran this whole branch and issued another retry of req.
          return auth.user$.pipe(
            take(1),
            switchMap(user => {
              if (user && user.uid !== uid) {
                // The session changed between dispatch and this 401, so the request is a
                // stranded artefact of the old one: req's URL, body and intent were all
                // authored for that identity. Retrying would replay it with the new account's
                // credentials — creating a payment or cancelling a subscription against the
                // wrong user. Surface the 401 and leave the healthy new session alone.
                return throwError(() => error);
              }

              if (user) {
                // Refresh the token, then retry once with it. This used to wait on token$ for the
                // NEXT emission (skip(1)) and trigger the refresh from a tap placed after that
                // skip — so the refresh was expected to be caused by the very emission it was
                // waiting for, and updateToken() was never subscribed to anyway. Nothing ever
                // emitted and the caller hung forever.
                return auth.updateToken().pipe(
                  // updateToken() reports failure as null; the timeout covers a getIdToken() that
                  // never settles at all. Either way the original 401 reaches the caller.
                  timeout({ each: TOKEN_REFRESH_TIMEOUT_MS, with: () => of(null) }),
                  switchMap(refreshedToken => {
                    if (!refreshedToken) {
                      return throwError(() => error);
                    }

                    return next(
                      request.clone({
                        // Raw token, no 'Bearer ' prefix — the API verifies the whole header value
                        // as the token, so a prefixed one is rejected. Matches the main path above.
                        headers: request.headers.set('Authorization', refreshedToken),
                      })
                    ).pipe(
                      catchError(retryError => {
                        if (retryError.status !== 401) {
                          return throwError(() => retryError);
                        }

                        // A freshly minted token was rejected too, so this session is really
                        // gone — but check who is signed in first. The uid check above only
                        // covers a switch before the refresh; the app can just as well switch
                        // while the retry is in flight, and tearing down the account that
                        // happens to be signed in now would sign out a healthy session.
                        return auth.user$.pipe(
                          take(1),
                          switchMap(currentUser => {
                            if (currentUser && currentUser.uid !== uid) {
                              return throwError(() => retryError);
                            }

                            // logout() returns a cold observable — it has to be subscribed to.
                            auth.logout().subscribe();
                            router.navigate(['/authentication/login']);

                            return throwError(() => retryError);
                          })
                        );
                      })
                    );
                  })
                );
              }

              // Nobody signed in, whether this request was dispatched anonymously or the
              // session died under it.
              if (!isPublicEndpoint(req.url)) {
                // Guards don't re-run without navigation, so a dead session on a guarded page
                // is caught here.
                router.navigate(['/authentication/login']);
              }

              return throwError(() => error);
            })
          );
        })
      )
    )
  );
};
